import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, X, Mail, GitCompare, Check, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { vehiclePublicAPI } from '../lib/api';
import { priceText, mileageText } from '../lib/financeMath';
import { compareStore } from '../lib/compareStore';
import LanguageToggle from '../components/LanguageToggle';

const emailOk = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e || '');

export default function VehicleCompare() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [orgId] = useState(compareStore.activeOrg());
    const [rows, setRows] = useState(null);          // resolved vehicles
    const [loading, setLoading] = useState(true);
    const [emailOpen, setEmailOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);

    const load = () => {
        const ids = compareStore.list(orgId);
        if (!ids.length) { setRows([]); setLoading(false); return; }
        vehiclePublicAPI.compare(ids)
            .then((r) => setRows((r.data?.vehicles || []).filter((x) => x.found && x.vehicle)))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const dealership = rows?.[0]?.dealership;
    const featureUnion = useMemo(() => {
        const s = [];
        (rows || []).forEach((r) => (r.vehicle.features || []).forEach((f) => { if (!s.includes(f)) s.push(f); }));
        return s;
    }, [rows]);

    const remove = (cardId) => {
        compareStore.remove(orgId, cardId);
        setRows((rs) => rs.filter((r) => r.card_id !== cardId));
        toast(t('vehicleExp.removedToast', 'Removed from comparison'));
    };

    const sendEmail = async () => {
        if (!emailOk(email)) { toast.error(t('compare.invalidEmail', 'Please enter a valid email')); return; }
        setSending(true);
        try {
            const ids = (rows || []).map((r) => r.card_id);
            const res = await vehiclePublicAPI.compareEmail({ card_ids: ids, email: email.trim(), lang: i18n.language || 'en' });
            toast.success(res.data?.email_sent ? t('compare.emailSent', 'Comparison sent to your email!') : t('compare.emailQueued', 'Comparison is on its way.'));
            setEmailOpen(false); setEmail('');
        } catch (err) { toast.error(err.response?.data?.detail || t('vehicleExp.err.generic', 'Something went wrong')); }
        finally { setSending(false); }
    };

    if (loading) return <div className="min-h-screen bg-neutral-950 flex items-center justify-center text-neutral-400" data-testid="cmp-loading">{t('common.loading', 'Loading…')}</div>;

    const specRow = (label, get, tid) => (
        <tr className="border-b border-white/5" data-testid={tid}>
            <td className="sticky left-0 z-10 bg-neutral-950 text-[11px] uppercase tracking-wide text-neutral-500 py-3 pr-3 align-top w-28">{label}</td>
            {rows.map((r) => <td key={r.card_id} className="py-3 px-3 text-sm text-neutral-100 align-top min-w-[150px]">{get(r.vehicle) || '—'}</td>)}
        </tr>
    );

    return (
        <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="vehicle-compare-page">
            <div className="sticky top-0 z-30 backdrop-blur-xl bg-neutral-950/80 border-b border-white/10">
                <div className="max-w-3xl mx-auto flex items-center justify-between px-4 h-14">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-neutral-300 hover:text-white" data-testid="cmp-back"><ArrowLeft className="w-4 h-4" />{t('compare.continueShopping', 'Continue shopping')}</button>
                    <div className="flex items-center gap-3">
                        {dealership && <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-amber-300/90 hidden sm:block">{dealership}</span>}
                        <LanguageToggle />
                    </div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 pb-32 pt-5">
                <h1 className="text-2xl font-extrabold flex items-center gap-2"><GitCompare className="w-6 h-6 text-amber-400" />{t('compare.title', 'Compare Vehicles')}</h1>

                {(!rows || rows.length === 0) ? (
                    <div className="flex flex-col items-center text-center gap-3 py-24" data-testid="cmp-empty">
                        <Car className="w-14 h-14 text-neutral-700" />
                        <p className="text-neutral-400 max-w-xs">{t('compare.empty', 'Your comparison is empty. Scan a vehicle on the lot and tap Compare to add it here.')}</p>
                        <Button variant="outline" className="border-neutral-700 bg-transparent" onClick={() => navigate(-1)}>{t('compare.continueShopping', 'Continue shopping')}</Button>
                    </div>
                ) : (
                    <>
                        <p className="text-sm text-neutral-400 mt-1">{t('compare.count', '{{n}} vehicles side-by-side', { n: rows.length })}</p>

                        <div className="mt-5 overflow-x-auto -mx-4 px-4" data-testid="cmp-table-wrap">
                            <table className="border-collapse">
                                <thead>
                                    <tr>
                                        <th className="sticky left-0 z-10 bg-neutral-950 w-28" />
                                        {rows.map((r) => {
                                            const vv = r.vehicle;
                                            return (
                                                <th key={r.card_id} className="p-2 align-top min-w-[150px]" data-testid="cmp-col">
                                                    <div className="relative rounded-xl overflow-hidden bg-white/[0.03] border border-white/10">
                                                        <button onClick={() => remove(r.card_id)} className="absolute top-1.5 right-1.5 z-10 bg-black/60 rounded-full p-1 hover:bg-black/80" data-testid={`cmp-remove-${r.card_id}`}><X className="w-3.5 h-3.5" /></button>
                                                        <div className="aspect-[4/3] bg-neutral-900">
                                                            {vv.photos?.[0] ? <img src={vv.photos[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-700"><Car className="w-8 h-8" /></div>}
                                                        </div>
                                                        <button onClick={() => navigate(`/vehicle/${r.card_id}`)} className="block w-full text-left p-2.5" data-testid={`cmp-open-${r.card_id}`}>
                                                            <p className="text-[13px] font-bold leading-tight text-neutral-100">{`${vv.year || ''} ${vv.make || ''} ${vv.model || ''}`.trim()}</p>
                                                            <p className="text-amber-400 font-extrabold text-base mt-0.5">{priceText(vv.asking_price)}</p>
                                                        </button>
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {specRow(t('compare.f.price', 'Price'), (vv) => priceText(vv.asking_price), 'cmp-row-price')}
                                    {specRow(t('compare.f.mileage', 'Mileage'), (vv) => mileageText(vv.mileage), 'cmp-row-mileage')}
                                    {specRow(t('compare.f.colour', 'Colour'), (vv) => vv.colour, 'cmp-row-colour')}
                                    {specRow(t('compare.f.trim', 'Trim'), (vv) => vv.trim, 'cmp-row-trim')}
                                    {specRow(t('compare.f.stock', 'Stock #'), (vv) => vv.stock_number, 'cmp-row-stock')}

                                    {featureUnion.length > 0 && (
                                        <tr><td colSpan={rows.length + 1} className="pt-5 pb-1 text-[11px] uppercase tracking-wider text-neutral-500 sticky left-0 bg-neutral-950">{t('usedVehicles.f.features', 'Vehicle Features')}</td></tr>
                                    )}
                                    {featureUnion.map((feat) => (
                                        <tr key={feat} className="border-b border-white/5" data-testid="cmp-feature-row">
                                            <td className="sticky left-0 z-10 bg-neutral-950 text-xs text-neutral-300 py-2.5 pr-3 align-middle w-28">{feat}</td>
                                            {rows.map((r) => (
                                                <td key={r.card_id} className="py-2.5 px-3 min-w-[150px]">
                                                    {(r.vehicle.features || []).includes(feat)
                                                        ? <Check className="w-4 h-4 text-emerald-400" data-testid="cmp-feat-yes" />
                                                        : <span className="text-neutral-600" data-testid="cmp-feat-no">—</span>}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Sticky email/continue dock */}
            {rows && rows.length > 0 && (
                <div className="fixed bottom-0 inset-x-0 z-30 border-t border-white/10 bg-neutral-950/90 backdrop-blur-xl">
                    <div className="max-w-3xl mx-auto p-3 flex gap-2">
                        <Button variant="outline" className="flex-1 h-12 border-white/15 bg-white/[0.03] text-neutral-100 hover:bg-white/10" onClick={() => navigate(-1)} data-testid="cmp-continue">{t('compare.continueShopping', 'Continue shopping')}</Button>
                        <Button className="flex-1 h-12 bg-amber-500 hover:bg-amber-400 text-black font-bold" onClick={() => setEmailOpen(true)} data-testid="cmp-email-open"><Mail className="w-5 h-5 mr-2" />{t('compare.emailMine', 'Email my comparison')}</Button>
                    </div>
                </div>
            )}

            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                <DialogContent className="sm:max-w-sm bg-neutral-900 text-neutral-100 border-neutral-800" data-testid="cmp-email-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('compare.emailTitle', 'Email my comparison')}</DialogTitle>
                        <DialogDescription className="text-neutral-400 text-sm">{t('compare.emailBody', "We'll send this comparison to your inbox with a link back to each vehicle.")}</DialogDescription>
                    </DialogHeader>
                    <Input type="email" placeholder={t('vehicleExp.fEmailReq', 'Email')} value={email} onChange={(e) => setEmail(e.target.value)} className="bg-neutral-800 border-neutral-700" data-testid="cmp-email-input" />
                    <Button className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold" onClick={sendEmail} disabled={sending} data-testid="cmp-email-send">{sending ? t('common.saving', 'Saving…') : t('compare.sendToMe', 'Send to me')}</Button>
                </DialogContent>
            </Dialog>
        </div>
    );
}
